import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  Button,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Calendar, Download, RefreshCcw, User, DollarSign } from "lucide-react";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";

const monthLabels = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const currency = (v = 0) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(Number(v || 0));

const EmployeeSalaryHistory = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());

  const years = useMemo(() => {
    const start = now.getFullYear() - 5;
    return Array.from({ length: 7 }, (_, i) => start + i);
  }, [now]);

  const [loading, setLoading] = useState(false);
  const [payouts, setPayouts] = useState([]);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState({ count: 0, month_total: 0 });

  const fetchMonthPayouts = useCallback(async (employeeId, y, mIndex) => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/salary/payouts/${employeeId}`, {
        params: { year: y, month: mIndex + 1 },
      });
      const data = res.data || {};
      const rows = Array.isArray(data.payouts) ? data.payouts : [];
      rows.sort((a, b) => (a.pay_monday < b.pay_monday ? -1 : 1));
      setPayouts(rows);
      setSummary({ count: data.count || rows.length, month_total: data.month_total || 0 });
    } catch (e) {
      console.error(e);
      setError("Erro ao carregar payouts do mês.");
      setPayouts([]);
      setSummary({ count: 0, month_total: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    fetchMonthPayouts(user.id, year, month);
  }, [user?.id, year, month, fetchMonthPayouts]);

  const handleReload = () => {
    if (!user?.id) return;
    fetchMonthPayouts(user.id, year, month);
  };

  const totals = useMemo(() => {
    const hours = payouts.reduce((acc, p) => acc + (p.hours || 0), 0);
    const hourly_total = payouts.reduce((acc, p) => acc + (p.hourly_total || 0), 0);
    const commission = payouts.reduce((acc, p) => acc + (p.commission || 0), 0);
    const bonus = payouts.reduce((acc, p) => acc + (p.charm_bonus_total || 0), 0);
    const tips = payouts.reduce((acc, p) => acc + (p.tip || 0), 0);
    const total = payouts.reduce((acc, p) => acc + (p.total || 0), 0);
    const avgRate = hours ? (hourly_total + commission + bonus + tips) / hours : 0;
    return { hours, hourly_total, commission, bonus, tips, total, avgRate };
  }, [payouts]);

  return (
    <Box
      sx={{
        p: 2,
        maxWidth: 1000,
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 2
      }}
    >
      <Paper
        elevation={0}
        className="print-hide"
        sx={{
          px: 2,
          py: 2,
          borderRadius: 3,
          backgroundColor: "rgba(255,255,255,0.9)",
          boxShadow: "0 6px 24px rgba(0,0,0,0.08)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center"
        }}
      >

        <Grid container spacing={1} alignItems="center" justifyContent="center" sx={{ maxWidth: 1000 }}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="month-select-label">Month</InputLabel>
              <Select
                labelId="month-select-label"
                value={month}
                label="Month"
                onChange={(e) => setMonth(Number(e.target.value))}
                startAdornment={<Calendar size={18} style={{ marginRight: 8 }} />}
              >
                {monthLabels.map((m, i) => (
                  <MenuItem key={m} value={i}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="year-select-label">Year</InputLabel>
              <Select
                labelId="year-select-label"
                value={year}
                label="Year"
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
              <Tooltip title="Reload">
                <span>
                  <IconButton onClick={handleReload} disabled={loading}>
                    <RefreshCcw size={18} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>

        <Grid item xs={12} sx={{ mt: 2, width: "100%", display: "flex", justifyContent: "center" }}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
              border: "1px solid #eaeaea",
              width: { xs: "100%", sm: "40%" },
            }}
          >
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 2 }, textAlign: "center" }}>
              <Typography variant="subtitle2" color="text.secondary">Viewing Period:</Typography>
              <Typography variant="body1" fontWeight="medium">
                {monthLabels[month]} {year}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Paper>

      <Grid container spacing={1} justifyContent="center">
        <Grid item xs={12} sm={10} md={5} sx={{ maxWidth: 1000}}>
        </Grid>

          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card sx={{ textAlign: "center", borderRadius: 3, boxShadow: "0 8px 26px rgba(0,0,0,0.06)", width: { xs: '100%', md: 1000 } }}>
            <CardContent>
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Total Earnings</Typography>
                  <Typography variant="h6" fontWeight={800} color="success.main">
                    {currency(summary.month_total)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">Avg Hourly Rate</Typography>
                  <Typography variant="h6" fontWeight={800}>
                    {currency(totals.avgRate)}
                  </Typography>
                </Grid>
              </Grid>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>{(totals.hours ?? 0).toFixed(2)}</strong> hrs •
                Hourly: <strong>{currency(totals.hourly_total)}</strong> •
                Comm: <strong>{currency(totals.commission)}</strong> •
                Bonus: <strong>{currency(totals.bonus)}</strong> •
                Tips: <strong>{currency(totals.tips)}</strong>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper
        className="printable-content"
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 8px 26px rgba(0,0,0,0.06)",
          backgroundColor: "#fff",
          mx: "auto",
          width: "100%"
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="error" variant="body1">{error}</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f7f7f7' }}>
                  <TableCell><strong>Date Paid</strong></TableCell>
                  <TableCell><strong>Calculation Period</strong></TableCell>
                  <TableCell align="right"><strong>Hours</strong></TableCell>
                  <TableCell align="right"><strong>Hourly Pay</strong></TableCell>
                  <TableCell align="right"><strong>Commission</strong></TableCell>
                  <TableCell align="right"><strong>Bonus</strong></TableCell>
                  <TableCell align="right"><strong>Tips</strong></TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}><strong>Avg Rate</strong></TableCell>
                  <TableCell align="right"><strong>Total</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payouts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No payouts found for {monthLabels[month]} {year}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  payouts.map((p) => (
                    <TableRow
                      key={p.pay_monday}
                      hover
                      sx={{
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                        '@media print': { '&:hover': { bgcolor: 'transparent' } }
                      }}
                    >
                      <TableCell>
                        {new Date(p.pay_monday + "T00:00:00").toLocaleDateString("en-US", {
                          month: "2-digit", day: "2-digit", year: "numeric"
                        })}
                      </TableCell>
                      <TableCell>
                        {new Date(p.period_start + "T00:00:00").toLocaleDateString("en-US", {
                          month: "2-digit", day: "2-digit", year: "numeric"
                        })}
                        {" to "}
                        {new Date(p.period_end + "T00:00:00").toLocaleDateString("en-US", {
                          month: "2-digit", day: "2-digit", year: "numeric"
                        })}
                      </TableCell>
                      <TableCell align="right">{(p.hours ?? 0).toFixed(2)}</TableCell>
                      <TableCell align="right">{currency(p.hourly_total)}</TableCell>
                      <TableCell align="right">{currency(p.commission)}</TableCell>
                      <TableCell align="right">{currency(p.charm_bonus_total)}</TableCell>
                      <TableCell align="right">{currency(p.tip)}</TableCell>
                      <TableCell align="right">{currency(p.avg_rate)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {currency(p.total)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ bgcolor: '#f7f7f7' }}>
                  <TableCell colSpan={8} align="right" sx={{ fontWeight: 'bold' }}>
                    Month Total:
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {currency(summary.month_total)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default EmployeeSalaryHistory;
